import { useRef, type FC, type ReactNode } from 'react';
import { IconButton, Layer, Link, Slider } from '@carbon/react';
import {
  Close, PauseFilled, PlayFilledAlt, SkipBackFilled, SkipForwardFilled, VolumeMute, VolumeUp,
} from '@carbon/icons-react';

import { formatarTempo } from '../../logica/player/usePlayerLocal';
import styles from './BarraDoPlayer.module.scss';

// A BARRA DO PLAYER — o desenho do player do rodapé, sem o áudio: anterior/tocar/próxima, a faixa,
// o progresso (Slider do Carbon, com o tempo nas pontas), tirar o som e fechar. O player global da
// casca (`casca/Player`) e a playlist compartilhada (`/ouvir`) usam ESTA barra: o mesmo player em
// todo lugar (pedido do Lucas, 03/10/2026). O `<audio>` vem como filho, de quem toca.

export interface PropsDaBarraDoPlayer {
  titulo: string;
  apoio?: string | null;
  /** Com ele, o título é um link (volta para a tela da faixa). */
  abrirFaixa?: { href: string; rotulo: string; abrir: () => void };
  tocando: boolean;
  tocavel: boolean;
  varias: boolean;
  tempo: number;
  duracao: number;
  mudo: boolean;
  alternar: () => void;
  anterior: () => void;
  proxima: () => void;
  irPara: (segundos: number) => void;
  alternarMudo: () => void;
  /** Botões a mais antes do fechar (ex.: tela cheia). */
  extras?: ReactNode;
  fechar?: () => void;
  /** No celular, o progresso desce para uma segunda linha em vez de sumir (a página pública). */
  progressoNoCelular?: boolean;
  className?: string;
  /** O `<audio>` de quem toca. */
  children?: ReactNode;
}

export const BarraDoPlayer: FC<PropsDaBarraDoPlayer> = (p) => {
  // ⚠️ O SLIDER DO CARBON AVISA `onChange` A CADA MUDANÇA DE VALOR, inclusive a que vem do próprio
  // áudio (o `timeupdate`, várias vezes por segundo). Seguir todo aviso fazia o áudio voltar ao
  // ponto arredondado do passo a cada instante, e ele picotava. Só se pula quando é a pessoa que
  // mexe (mouse, toque ou teclado no controle).
  const mexendo = useRef(false);
  const classes = [styles.barra, p.progressoNoCelular ? styles.comProgressoNoCelular : '', p.className ?? ''].filter(Boolean).join(' ');

  return (
    <Layer as='section' aria-label='Player' className={classes}>
      {p.children}

      <div className={styles.controles}>
        <IconButton kind='ghost' size='md' label='Anterior' onClick={p.anterior} disabled={!p.varias}>
          <SkipBackFilled />
        </IconButton>
        <IconButton
          kind='primary'
          size='md'
          label={p.tocavel ? (p.tocando ? 'Pausar' : 'Tocar') : 'Áudio pendente'}
          onClick={p.alternar}
          disabled={!p.tocavel}
        >
          {p.tocando ? <PauseFilled /> : <PlayFilledAlt />}
        </IconButton>
        <IconButton kind='ghost' size='md' label='Próxima' onClick={p.proxima} disabled={!p.varias}>
          <SkipForwardFilled />
        </IconButton>
      </div>

      <div className={styles.faixa}>
        {p.abrirFaixa ? (
          <Link
            href={p.abrirFaixa.href}
            className={styles.titulo}
            aria-label={p.abrirFaixa.rotulo}
            onClick={(e: React.MouseEvent) => { e.preventDefault(); p.abrirFaixa?.abrir(); }}
          >
            {p.titulo}
          </Link>
        ) : (
          <span className={`${styles.titulo} ${styles.tituloSemLink}`}>{p.titulo}</span>
        )}
        {p.apoio && <span className={styles.apoio}>{p.apoio}</span>}
      </div>

      <div
        className={styles.progresso}
        onPointerDownCapture={() => { mexendo.current = true; }}
        onKeyDownCapture={() => { mexendo.current = true; }}
        // O aviso do Carbon chega depois do render (num efeito): o fim do gesto espera um instante.
        onPointerUpCapture={() => { setTimeout(() => { mexendo.current = false; }, 250); }}
        onKeyUpCapture={() => { setTimeout(() => { mexendo.current = false; }, 250); }}
      >
        <Slider
          labelText='Progresso'
          hideLabel
          hideTextInput
          min={0}
          max={Math.max(p.duracao, 1)}
          step={1}
          value={p.tempo}
          // A ponta da esquerda é o tempo tocado agora (o mínimo, 0, nunca muda); a da direita, a
          // duração.
          formatLabel={(v: number) => formatarTempo(v === 0 ? p.tempo : v)}
          // Dentro do gesto, o eco do próprio áudio (menos de 1 s do tempo atual) também não pula.
          onChange={({ value }: { value: number }) => { if (mexendo.current && Math.abs(value - p.tempo) >= 0.9) p.irPara(value); }}
          disabled={!p.tocavel}
        />
      </div>

      <div className={styles.fim}>
        <IconButton kind='ghost' size='md' label={p.mudo ? 'Ativar som' : 'Tirar o som'} onClick={p.alternarMudo} className={styles.som}>
          {p.mudo ? <VolumeMute /> : <VolumeUp />}
        </IconButton>
        {p.extras}
        {p.fechar && (
          <IconButton kind='ghost' size='md' label='Fechar o player' align='top-end' onClick={p.fechar}>
            <Close />
          </IconButton>
        )}
      </div>
    </Layer>
  );
};
